import { Button } from '@/components/ui/button';
import { IconSymbol } from '@/components/ui/icon-symbol';
import { Portal } from '@/components/ui/portal';
import { TextInputField } from '@/components/ui/text-input-field';
import { View, AnimatedView } from '@/components/ui/tw-wrappers';
import { Colors } from '@/constants/theme';
import { functions, httpsCallable, track } from '@/lib/firebase';
import { useScenarioStore } from '@/stores/scenario-store';
import { Country } from '@/types/country';
import { SendReportRequestData } from '@/types/model';
import type { TransferPair } from '@/types/transfer-pair';
import { useFormatters } from '@/utils/formatters/useFormatters';
import { openContactScheduling } from '@/utils/mail';
import { buildComparePayload } from '@/utils/report-payloads';
import { useCountryNameById } from '@/hooks/useCountryName';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Pressable, Platform } from 'react-native';
import Animated, { FadeIn, FadeOut, FadeInUp, FadeOutDown } from 'react-native-reanimated';
import { z } from 'zod';
import { Text } from './text';

const schedulingSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type SchedulingFormValues = z.infer<typeof schedulingSchema>;

interface SchedulingModalProps {
  isVisible: boolean;
  onClose: () => void;
  data: TransferPair;
  countries: Country[];
}

export function SchedulingModal({ isVisible, onClose, data, countries }: SchedulingModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { formatCurrency, formatPercentage, formatNumber } = useFormatters();
  const formatters = { formatCurrency, formatPercentage, formatNumber };
  const inputs = useScenarioStore(s => s.inputs);
  const visibleScenarios = useScenarioStore(s => s.visibleScenarios);

  const getCountryName = useCountryNameById(countries);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<SchedulingFormValues>({
    resolver: zodResolver(schedulingSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  if (!isVisible) return null;

  const onSubmit = async (values: SchedulingFormValues) => {
    setLoading(true);
    try {
      // 1. Build and Send Report
      const sendReport = httpsCallable<SendReportRequestData, unknown>(functions, 'emailReport');
      const payload = await buildComparePayload({
        values: { name: 'User', email: values.email }, // Default name to 'User'
        inputs,
        data,
        visibleScenarios,
        getCountryName,
        t,
        formatters,
      });

      await sendReport({ email: values.email, emailPayload: payload, modelType: 'compare' });
      track('scheduling_report_sent', { email: values.email });

      // 2. Open Scheduling URL
      await openContactScheduling();
      
      // 3. Close Modal
      onClose();
    } catch (err) {
      console.error('Error in SchedulingModal onSubmit:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Portal>
      <View style={StyleSheet.absoluteFill} className="items-center justify-center">
        {/* Backdrop */}
        <AnimatedView
          entering={FadeIn.duration(300)}
          exiting={FadeOut.duration(200)}
          style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0,0,0,0.5)' }]}
        >
          <Pressable style={{ flex: 1 }} onPress={onClose} />
        </AnimatedView>

        {/* Modal content */}
        <AnimatedView
          entering={FadeInUp.springify()}
          exiting={FadeOutDown.duration(200)}
          style={styles.modalContainer}
          className="bg-white rounded-3xl p-8 shadow-2xl w-[90%] max-w-[480px]"
        >
          <View className="items-center mb-6">
            <View 
                style={[styles.iconContainer, { width: 64, height: 64, borderRadius: 32 }]} 
                className="items-center justify-center mb-4"
            >
              <IconSymbol name="calendar" size={32} color={Colors.light.primary} />
            </View>
            <Text type="title" className="text-center mb-2">
              {t('cta.schedule.title')}
            </Text>
            <Text className="text-center text-gray-500 leading-5">
              {t('cta.schedule.description')}
            </Text>
          </View>

          <View className="gap-6">
            <Controller
              control={control}
              name="email"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <View className="gap-1">
                  <TextInputField
                    id="schedule-email"
                    name="email"
                    label={t('home.reportForm.emailLabel')}
                    placeholder={t('home.reportForm.emailPlaceholder')}
                    value={value}
                    onChangeText={onChange}
                    disabled={loading}
                  />
                  {error && <Text style={styles.errorText}>{error.message}</Text>}
                </View>
              )}
            />

            <Button
              label={loading ? t('common.sending') : t('cta.schedule.button')}
              onPress={handleSubmit(onSubmit)}
              disabled={loading || !isValid}
              style={styles.actionButton}
            />
            
            <Pressable onPress={onClose} disabled={loading}>
                <Text className="text-center text-gray-400 font-medium">
                    {t('common.cancel')}
                </Text>
            </Pressable>
          </View>
        </AnimatedView>
      </View>
    </Portal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    zIndex: 1000,
  },
  iconContainer: {
    backgroundColor: 'rgba(11, 95, 255, 0.1)',
  },
  actionButton: {
    width: '100%',
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#DC2626',
    fontSize: 12,
    marginLeft: 4,
  }
});
